import {
  addDoc,
  CollectionReference,
  DocumentData,
  getDocs,
  limit,
  query,
  where,
} from 'firebase/firestore'
import {
  InventoryIdentity,
  inventoryDuplicateMessage,
  normalizeStockNumber,
  normalizeVin,
} from './inventoryUniqueness'

const createsInFlight = new Map<string, Promise<void>>()

function lookupValues(raw: unknown, normalize: (value: unknown) => string): string[] {
  const trimmed = String(raw ?? '').trim()
  const normalized = normalize(raw)
  return [...new Set([trimmed, normalized].filter(Boolean))]
}

async function matchingIdentities(
  collectionRef: CollectionReference<DocumentData>,
  field: 'stock_number' | 'vin',
  values: string[],
  max: number,
): Promise<InventoryIdentity[]> {
  const rows: InventoryIdentity[] = []
  for (const value of values) {
    const snap = await getDocs(query(collectionRef, where(field, '==', value), limit(max)))
    snap.forEach(docSnap => {
      rows.push(docSnap.data() as InventoryIdentity)
    })
  }
  return rows
}

export class DuplicateInventoryError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'DuplicateInventoryError'
  }
}

/**
 * Creates an inventory doc only when the stock number is unused and the VIN
 * is not already on an unsold truck. The form's in-memory list is paused
 * while the add dialog is open, so this read happens at write time.
 */
export async function addInventoryDocument(
  collectionRef: CollectionReference<DocumentData>,
  data: DocumentData,
): Promise<string> {
  const key = `${collectionRef.path}|${normalizeStockNumber(data.stock_number)}|${normalizeVin(data.vin)}`
  const previous = createsInFlight.get(key) ?? Promise.resolve()
  let release: () => void = () => {}
  const gate = new Promise<void>(resolve => {
    release = resolve
  })
  const tail = previous.then(() => gate)
  createsInFlight.set(key, tail)
  await previous

  try {
    const existing = [
      ...(await matchingIdentities(
        collectionRef,
        'stock_number',
        lookupValues(data.stock_number, normalizeStockNumber),
        5,
      )),
      ...(await matchingIdentities(collectionRef, 'vin', lookupValues(data.vin, normalizeVin), 25)),
    ]
    const message = inventoryDuplicateMessage(data, existing)
    if (message) throw new DuplicateInventoryError(message)

    const docRef = await addDoc(collectionRef, data)
    return docRef.id
  } finally {
    release()
    if (createsInFlight.get(key) === tail) createsInFlight.delete(key)
  }
}
